import { type FocusEventHandler, type FormEventHandler, useCallback } from 'react';

import type { ErrorMessageMap } from './types';

const defaultErrorMessageMap: ErrorMessageMap = {
  valueMissing: 'This field is required',
  tooLong: 'Input is too long',
  tooShort: 'Input is too short',
  rangeOverflow: 'Value is too large',
  rangeUnderflow: 'Value is too small',
  typeMismatch: 'Value does not fit the input type',
};

function getErrorString(validityState: ValidityState, providedErrorMessageMap?: ErrorMessageMap): string {
  const errorMessageMap = { ...defaultErrorMessageMap, ...providedErrorMessageMap };

  const errorSlices = Object.entries(errorMessageMap) as [keyof ValidityState, string][];
  const errorStringParts = errorSlices.reduce<string[]>((result, [key, part]) => {
    if (validityState[key] && part) {
      result.push(part);
    }

    return result;
  }, []);

  return errorStringParts.join('');
}

export function useBuiltInValidation(
  setErrorMessage: (errorMessage?: string) => void,
  errorMessageMap?: ErrorMessageMap,
  baseHandlers?: Partial<{
    onBlur: FocusEventHandler<HTMLInputElement>;
    onInvalid: FormEventHandler<HTMLInputElement>;
  }>,
) {
  const { onInvalid, onBlur } = baseHandlers || {};

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (event) => {
      const isValid = event.target.reportValidity();

      if (isValid) {
        setErrorMessage(undefined);
      }

      onBlur?.(event);
    },
    [onBlur, setErrorMessage],
  );

  const handleInvalid = useCallback<FormEventHandler<HTMLInputElement>>(
    (event) => {
      event.preventDefault();
      setErrorMessage(getErrorString(event.currentTarget.validity, errorMessageMap));
      onInvalid?.(event);
    },
    [errorMessageMap, onInvalid, setErrorMessage],
  );

  return {
    handleBlur,
    handleInvalid,
  };
}
